/**
 * PraticaMadreReference — F-004 Phase 3 (IMPL-P3-006).
 *
 * Componente a 3 stati che risolve il riferimento alla pratica madre da cui
 * l'adempimento eredita i dati comuni:
 *
 *  - Stato A (parentId noto, via query param): mostra una Card di conferma con
 *    codicePratica (font-mono), StateBadge e riepilogo immobile/proprietario.
 *    Un link secondario "Cambia contratto di riferimento" scende allo stato B.
 *  - Stato B (nessun parentId — ricerca): Input di autocomplete sull'archivio
 *    delle pratiche `COMPLETATA` (riuso della search F-006 filtrata su
 *    `stato === 'COMPLETATA'`); i risultati usano lo stile di `PraticaListItem`;
 *    selezionando una riga si transita allo stato A.
 *  - Stato C (estremi manuali, UC-002 ext. 3a): disclosure sotto la ricerca;
 *    espandendola compaiono input manuali per gli estremi di registrazione e un
 *    `Alert` `role="status"` con il warning verbatim; in questo stato NON avviene
 *    alcun prefill.
 *
 * Design (parallelizzazione Phase 3/4): questo componente è **presentazionale/
 * controllato** — i dati (summary risolto, risultati di ricerca) e i side-effect
 * (fetch) sono di competenza del host (`AdempimentoInner`, Phase 4). Il host:
 *   - passa `resolvedSummary` quando parentId è noto o una riga è selezionata,
 *   - passa `searchResults` + `onSearch(query)` per lo stato B,
 *   - riceve `onResolved(parentDati)` quando A/B risolvono, per lanciare
 *     `prefillFromParent()`.
 * Ciò mantiene il componente testabile in isolamento e non lo accoppia a un hook
 * di fetch specifico (evita conflitti con Phase 2/5 in corso).
 *
 * Pattern seguiti (plan IMPL-P3-006):
 *  - usePraticaDetail.ts (risoluzione parent quando parentId noto → qui il dato
 *    risolto arriva già dal host, coerente con la separazione page/inner).
 *  - PraticaListItem.tsx (stile riga risultato di ricerca).
 */
import { useEffect, useRef, useState } from 'react';
import type { ContractDraft } from '@app/shared';
import type { PraticaListItem as PraticaListItemType } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

/** Warning verbatim (spec UC-002 ext. 3a) mostrato nello stato manuale. */
export const MANUAL_ESTREMI_WARNING =
  'Contratto non collegato all’archivio — gli operatori potrebbero richiedere integrazioni.';

/** Riepilogo minimo della pratica madre risolta (stato A). */
export interface ResolvedParentSummary {
  id: string;
  codicePratica: string;
  stato: string;
  immobileLabel: string;
  proprietarioLabel: string;
  /** Dati strutturati del contratto madre, per il prefill. */
  parentDati: ContractDraft;
}

/** Estremi di registrazione inseriti manualmente (stato C — nessun prefill). */
export interface ManualEstremi {
  serie?: string;
  numero?: string;
  anno?: string;
  ufficio?: string;
}

export interface PraticaMadreReferenceProps {
  /**
   * Riepilogo della pratica madre risolta (stato A). Se presente, il componente
   * mostra la Card di conferma; altrimenti mostra la ricerca (stato B) + il
   * disclosure manuale (stato C).
   */
  resolvedSummary?: ResolvedParentSummary;
  /** Termine di ricerca corrente (stato B), controllato dal host. */
  searchQuery?: string;
  /** Callback digitazione nella ricerca (stato B). Il host filtra su COMPLETATA. */
  onSearch?: (query: string) => void;
  /** Risultati di ricerca (stato B), già filtrati su `stato === 'COMPLETATA'`. */
  searchResults?: ReadonlyArray<PraticaListItemType>;
  /** True mentre la ricerca è in corso (stato B). */
  isSearching?: boolean;
  /** Selezione di una riga di ricerca → transizione allo stato A. */
  onSelectResult?: (item: PraticaListItemType) => void;
  /** Torna dallo stato A allo stato B ("Cambia contratto di riferimento"). */
  onChangeReference?: () => void;
  /** Chiamato quando A/B risolvono, così il host lancia `prefillFromParent()`. */
  onResolved?: (parentDati: ContractDraft) => void;
  /** Cambiamento degli estremi manuali (stato C). NESSUN prefill in questo stato. */
  onManualEstremiChange?: (estremi: ManualEstremi) => void;
}

const SR_STATUS_ID = 'pratica-madre-search-status';

export function PraticaMadreReference({
  resolvedSummary,
  searchQuery = '',
  onSearch,
  searchResults = [],
  isSearching = false,
  onSelectResult,
  onChangeReference,
  onResolved,
  onManualEstremiChange,
}: PraticaMadreReferenceProps) {
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState<ManualEstremi>({});

  // Quando lo stato A risolve (resolvedSummary presente), notifica il host una
  // sola volta per id risolto, così può lanciare `prefillFromParent()`.
  const lastResolvedIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (resolvedSummary && lastResolvedIdRef.current !== resolvedSummary.id) {
      lastResolvedIdRef.current = resolvedSummary.id;
      onResolved?.(resolvedSummary.parentDati);
    }
  }, [resolvedSummary, onResolved]);

  // Stato A: pratica madre risolta.
  if (resolvedSummary) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Contratto di riferimento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm">{resolvedSummary.codicePratica}</span>
            <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium">
              {resolvedSummary.stato}
            </span>
          </div>
          <dl className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-muted-foreground">Immobile</dt>
              <dd>{resolvedSummary.immobileLabel}</dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">Proprietario</dt>
              <dd>{resolvedSummary.proprietarioLabel}</dd>
            </div>
          </dl>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-sm"
            onClick={() => {
              onChangeReference?.();
            }}
          >
            Cambia contratto di riferimento
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Stato B: ricerca archivio (+ Stato C: disclosure estremi manuali).
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="pratica-madre-search">Cerca il contratto di riferimento</Label>
        <Input
          id="pratica-madre-search"
          type="search"
          role="combobox"
          aria-expanded={searchResults.length > 0}
          aria-controls="pratica-madre-results"
          aria-describedby={SR_STATUS_ID}
          autoComplete="off"
          placeholder="Codice pratica, nominativo o indirizzo…"
          value={searchQuery}
          onChange={(e) => onSearch?.(e.target.value)}
        />
        <p id={SR_STATUS_ID} role="status" aria-live="polite" className="sr-only">
          {isSearching
            ? 'Ricerca in corso…'
            : searchQuery
              ? `${searchResults.length} risultati`
              : ''}
        </p>
      </div>

      {searchResults.length > 0 && (
        <ul
          id="pratica-madre-results"
          className="divide-y rounded-md border"
          aria-label="Risultati della ricerca"
        >
          {searchResults.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onSelectResult?.(item)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted/30"
              >
                <span className="font-mono text-xs">{item.id.slice(-8)}</span>
                <span className="flex-1 truncate">
                  {item.indirizzoImmobile} — {item.proprietarioNome}
                </span>
                <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs">
                  {item.stato}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Stato C: disclosure estremi manuali — nessun prefill (onResolved MAI chiamato qui) */}
      <div>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          aria-expanded={manualOpen}
          aria-controls="pratica-madre-manual"
          onClick={() => setManualOpen((v) => !v)}
        >
          Il contratto non è nell&rsquo;archivio? Inserisci gli estremi manualmente
        </Button>

        {manualOpen && (
          <div id="pratica-madre-manual" className="mt-3 space-y-3">
            <Alert role="status" className="border-blue-300 text-blue-800">
              <AlertDescription>{MANUAL_ESTREMI_WARNING}</AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(
                [
                  ['serie', 'Serie'],
                  ['numero', 'Numero'],
                  ['anno', 'Anno'],
                  ['ufficio', 'Ufficio'],
                ] as ReadonlyArray<[keyof ManualEstremi, string]>
              ).map(([key, label]) => (
                <div key={key} className="space-y-1">
                  <Label htmlFor={`estremi-${key}`}>{label}</Label>
                  <Input
                    id={`estremi-${key}`}
                    value={manual[key] ?? ''}
                    onChange={(e) => {
                      const next = { ...manual, [key]: e.target.value };
                      setManual(next);
                      onManualEstremiChange?.(next);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
